import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection } from '../../runtime/mobile/useCollection';
import { createAdminClient } from '../../runtime/mobile/adminClient';
import { TEST_WORKER_URL } from '../../runtime/mobile/testWorkerClient';
import { promptText } from '../../ui/dialogs';
import { colors, fonts, radius, spacing, typography } from '../../config/theme';
import { Card, Loader, SectionLabel, screen, tint } from '../ui';
import { StickerBackdrop, Wordmark } from '../brand';

const rub = (n) => `${Math.round(n).toLocaleString('ru-RU')} ₽`;
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

function SalesRow({ label, bucket, refLabel }) {
  const d = bucket.deltaPct;
  const bits = [];
  if (bucket.avg != null) bits.push(`чек ${rub(bucket.avg)}`);
  if (d != null && d !== 0) bits.push(`${d > 0 ? '▲' : '▼'}${Math.abs(d)}%${refLabel ? ` ${refLabel}` : ''}`);
  return (
    <View style={styles.salesLine}>
      <Text style={styles.salesK}>{label}{bucket.count ? ` · ${bucket.count} чек.` : ''}</Text>
      <View style={styles.salesRight}>
        <Text style={styles.salesV}>{rub(bucket.revenue)}</Text>
        {bits.length ? (
          <Text style={[styles.salesDelta, d > 0 && styles.salesUp, d < 0 && styles.salesDown]}>{bits.join(' · ')}</Text>
        ) : null}
      </View>
    </View>
  );
}

const OPEN_STATES = new Set(['active', 'opening', 'ready_to_open', 'wake_up_pending', 'wake_up_confirmed', 'wake_up_escalated']);

// Little bit of personality on the owner dashboard — rotates by day so it
// doesn't get stale.
const GREETINGS = [
  'С возвращением, повелитель смен 👑',
  'На связи, создатель графика 🛠️',
  'Здорово, начальник вселенной ✨',
  'Приветствуем, главный по кофе и табаку ☕🚬',
  'Ты снова в деле, босс 💼',
];
const greeting = () => GREETINGS[new Date().getDate() % GREETINGS.length];

function Kpi({ n, label, kind }) {
  const color = { live: colors.primary, warn: tint.amber, bad: tint.rose }[kind] || colors.textPrimary;
  return (
    <View style={styles.kpi}>
      <Text style={[styles.kpiN, { color }]}>{n}</Text>
      <Text style={styles.kpiK}>{label}</Text>
    </View>
  );
}

function NavRow({ title, subtitle, onPress }) {
  return (
    <Card onPress={onPress} style={styles.navRow}>
      <View style={{ flex: 1 }}>
        <Text style={styles.navTitle}>{title}</Text>
        <Text style={styles.navSub}>{subtitle}</Text>
      </View>
      <Text style={styles.chevron}>›</Text>
    </Card>
  );
}

export default function AdminHomeScreen({ navigation }) {
  const { profile, user, isOwner } = useAuth();
  const client = useMemo(() => createAdminClient(user), [user]);
  const [sales, setSales] = useState(null);

  useEffect(() => {
    if (!isOwner || !user) return undefined;
    let alive = true;
    (async () => {
      try {
        const token = await user.getIdToken();
        const r = await fetch(`${TEST_WORKER_URL}/warehouse/sales-summary`, {
          method: 'POST', headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' }, body: '{}',
        });
        const j = await r.json().catch(() => ({}));
        if (alive && j && j.available) setSales(j);
      } catch { /* optional card */ }
    })();
    return () => { alive = false; };
  }, [isOwner, user]);
  const shifts = useCollection('shifts');
  const tasks = useCollection('shiftTasks');
  const checklists = useCollection('checklistTemplates');
  const taskTemplates = useCollection('taskTemplates');
  const schedules = useCollection('scheduleEntries');
  const users = useCollection('users');
  const locations = useCollection('locations');
  const shiftTypes = useCollection('shiftTypes');

  const stats = useMemo(() => {
    const openNow = shifts.docs.filter((s) => OPEN_STATES.has(s.status)).length;
    const overdue = tasks.docs.filter((t) => t.status === 'overdue').length;
    const missed = shifts.docs.filter((s) => s.status === 'missed').length;
    const upcoming = schedules.docs.filter((e) => e.status === 'planned').length;
    const activeUsers = users.docs.filter((u) => u.active).length;
    const inactiveUsers = users.docs.length - activeUsers;
    const gaps = schedules.docs.filter((e) => e.status === 'planned' && !e.currentEmployeeId).length;
    return { openNow, overdue, missed, upcoming, activeUsers, inactiveUsers, gaps };
  }, [shifts.docs, tasks.docs, schedules.docs, users.docs]);

  if (shifts.loading && !shifts.docs.length) return <Loader />;

  return (
    <View style={screen.root}>
      <StickerBackdrop seed="obzor" opacity={0.12} />
      <ScrollView contentContainerStyle={screen.content}>
      <View style={styles.brandRow}>
        <Wordmark height={20} color={colors.textPrimary} />
        <Text style={styles.eyebrow}>{greeting()}</Text>
      </View>
      <Pressable
        onPress={() => promptText('Как вас зовут? (видно в чате)', profile?.fullName, (name) => {
          if (user?.uid) client.updateUser(user.uid, { fullName: name }).catch(() => {});
        })}
      >
        <Text style={styles.hello}>{profile?.fullName || 'Создатель'} ✏️</Text>
      </Pressable>
      <View style={styles.kpis}>
        <Kpi n={stats.openNow} label="🔥 идут сейчас" kind="live" />
        <Kpi n={stats.overdue} label="⏰ просрочено" kind={stats.overdue ? 'warn' : undefined} />
        <Kpi n={stats.missed} label="😴 проспали" kind={stats.missed ? 'bad' : undefined} />
        <Kpi n={stats.upcoming} label="📅 в графике" />
      </View>

      {sales && sales.today ? (
        <Card style={styles.salesCard} onPress={() => navigation.navigate('Assistant')}>
          <Text style={styles.salesLabel}>💰 ВЫРУЧКА (МОЙСКЛАД)</Text>
          <SalesRow label="Сегодня" bucket={sales.today} refLabel="ко дню неделю назад" />
          <SalesRow label="7 дней" bucket={sales.week} refLabel="к прошлой неделе" />
          <SalesRow label={cap(sales.prevMonth.label)} bucket={sales.prevMonth} />
          <Text style={styles.salesFoot}>Разбор — в «Помощник»</Text>
        </Card>
      ) : null}

      <SectionLabel>Моя работа</SectionLabel>
      <NavRow
        title="🔥 Моя смена"
        subtitle="Открыть смену, задачи, закрытие — как у сотрудника"
        onPress={() => navigation.navigate('MyShift')}
      />
      <NavRow
        title="📅 Мой график"
        subtitle="Записаться на смену"
        onPress={() => navigation.navigate('MySchedule')}
      />

      <SectionLabel>Рычаги управления</SectionLabel>
      <NavRow
        title="📋 Чек-листы и задачи"
        subtitle={`${checklists.docs.length} наборов · ${taskTemplates.docs.filter((t) => t.active).length} задач`}
        onPress={() => navigation.navigate('Checklists')}
      />
      <NavRow
        title="🗓 График смен"
        subtitle={stats.gaps ? `${stats.upcoming} запланировано · ${stats.gaps} без сотрудника` : `${stats.upcoming} запланировано`}
        onPress={() => navigation.navigate('ScheduleAdmin')}
      />
      <NavRow
        title="🧑‍🍳 Сотрудники"
        subtitle={`${stats.activeUsers} активных${stats.inactiveUsers ? ` · ${stats.inactiveUsers} отключено` : ''}`}
        onPress={() => navigation.navigate('Employees')}
      />
      <NavRow
        title="🏪 Точки и типы смен"
        subtitle={`${locations.docs.length} точек · ${shiftTypes.docs.length} типов`}
        onPress={() => navigation.navigate('Locations')}
      />
      <NavRow
        title="📦 Сроки годности"
        subtitle="МойСклад · напитки, снеки, табак"
        onPress={() => navigation.navigate('ProductExpiries')}
      />
      <NavRow
        title="📊 Отчёты и отработка"
        subtitle="Закрытые смены и часы сотрудников"
        onPress={() => navigation.navigate('ShiftReports')}
      />
      <NavRow
        title="🤖 Помощник и аналитика"
        subtitle="ИИ-чат + разбор данных магазина за 30 дней"
        onPress={() => navigation.navigate('Assistant')}
      />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  brandRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm, marginBottom: spacing.xs },
  eyebrow: { ...typography.caption, color: colors.primary, fontWeight: '700', flexShrink: 1, textAlign: 'right' },
  hello: { ...typography.h1, color: colors.textPrimary },
  kpis: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.xs },
  kpi: { flexGrow: 1, flexBasis: '46%', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, padding: spacing.md },
  kpiN: { fontFamily: fonts.display, fontSize: 30, fontWeight: '800', letterSpacing: -0.5 },
  kpiK: { ...typography.caption, color: colors.textMuted, marginTop: 4 },
  salesCard: { marginTop: spacing.sm, gap: 2 },
  salesLabel: { ...typography.caption, color: colors.textMuted, fontWeight: '800', letterSpacing: 0.4, marginBottom: spacing.xs },
  salesLine: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', paddingVertical: 5 },
  salesK: { ...typography.caption, color: colors.textSecondary, flexShrink: 1, paddingRight: spacing.sm, paddingTop: 2 },
  salesRight: { alignItems: 'flex-end' },
  salesV: { fontFamily: fonts.display, fontSize: 18, fontWeight: '800', color: colors.textPrimary, letterSpacing: -0.5, fontVariant: ['tabular-nums'] },
  salesDelta: { ...typography.caption, color: colors.textMuted, fontSize: 11, marginTop: 1 },
  salesUp: { color: colors.success },
  salesDown: { color: tint.rose },
  salesFoot: { ...typography.caption, color: colors.textMuted, marginTop: spacing.xs },
  navRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  navTitle: { ...typography.h3, color: colors.textPrimary },
  navSub: { ...typography.caption, color: colors.textMuted, marginTop: 1 },
  chevron: { fontSize: 22, color: colors.textMuted },
});
